import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { X } from "lucide-react";
import toast from "react-hot-toast";

import type { CellAssignment, Subtype } from "../../types";
import type { PlayerState } from "../../hooks/useWebRTC";
import { useDevicesStore } from "../../store/devicesStore";
import { useGridStore } from "../../store/gridStore";
import { startStream, stopStream } from "../../api/streams";
import { WebRTCPlayer } from "../Player/WebRTCPlayer";
import { transcodeKey as makeTranscodeKey, readTranscodeFlag, writeTranscodeFlag, clearTranscodeFlag } from "../../lib/transcodeFlag";

interface Props {
  cell: CellAssignment;
  onMaximize: () => void;
}

interface LiveStream {
  streamId: string;
  streamName: string;
  subtype: Subtype;
  deviceId: number;
  channel: number;
  // True si este stream fue creado con transcode (HEVC→H.264 vía ffmpeg en go2rtc).
  // Va al matchesCell para que si cambia la flag, el slot viejo no matchee y
  // arranquemos uno nuevo con la nueva config.
  transcode: boolean;
}

type Slot = "A" | "B";

// Componente con double-buffer para evitar flash de "Conectando..." al cambiar
// calidad (subtype) o al maximizar. Dos <WebRTCPlayer> permanentes en el arbol
// DOM, uno por slot. Al pedir un nuevo stream lo cargamos en el slot OPUESTO al
// activo; cuando reporta "playing" hacemos swap CSS y detenemos el viejo. La
// transicion es visualmente instantanea — el video viejo sigue visible hasta
// que el nuevo realmente tenga frames.
export function GridCell({ cell, onMaximize }: Props) {
  const devices = useDevicesStore((s) => s.devices);
  const channelsByDevice = useDevicesStore((s) => s.channelsByDevice);
  const assignChannel = useGridStore((s) => s.assignChannel);
  const setStreamInStore = useGridStore((s) => s.setStream);
  const setSubtype = useGridStore((s) => s.setSubtype);
  const clearCell = useGridStore((s) => s.clearCell);
  const cells = useGridStore((s) => s.cells);
  const fillCells = useGridStore((s) => s.fillCells);
  const selectedCellId = useGridStore((s) => s.selectedCellId);
  const setSelectedCell = useGridStore((s) => s.setSelectedCell);
  const isSelected = selectedCellId === cell.cellId;
  const fitMode: "contain" | "fill" = fillCells ? "fill" : "contain";

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [slotA, setSlotA] = useState<LiveStream | null>(null);
  const [slotB, setSlotB] = useState<LiveStream | null>(null);
  const [activeSlot, setActiveSlot] = useState<Slot>("A");
  // Flag de "este canal necesita transcoding HEVC→H.264". Se activa cuando un
  // slot WebRTCPlayer dispara onDecodeError. Persiste por (deviceId, channel)
  // en localStorage con TTL: si este browser ya descubrio que el canal es
  // HEVC indecodable, los proximos opens arrancan DIRECTO con transcode y
  // evitan el round-trip "intentar HEVC → fail → reintentar transcode" (~4-6s).
  // TTL evita que un canal con VPN temporalmente caida quede atrapado en
  // transcode forever (peor caso: ffmpeg corre desperdiciado por hasta 7 dias).
  const transcodeKey = makeTranscodeKey(cell.deviceId, cell.channel);
  const [transcode, setTranscode] = useState<boolean>(() => readTranscodeFlag(transcodeKey));
  useEffect(() => {
    setTranscode(readTranscodeFlag(transcodeKey));
  }, [transcodeKey, cell.subtype]);

  // Refs para acceso desde cleanup (al desmontar, los closures de useEffect
  // capturan los slots vacios sino).
  const slotARef = useRef<LiveStream | null>(null);
  const slotBRef = useRef<LiveStream | null>(null);
  slotARef.current = slotA;
  slotBRef.current = slotB;

  const matchesCell = (s: LiveStream | null): boolean => {
    if (!s) return false;
    return s.deviceId === cell.deviceId
      && s.channel === cell.channel
      && s.subtype === cell.subtype
      && s.transcode === transcode;
  };

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ["CHANNEL", "DEVICE"],
      collect: (m) => ({ isOver: m.isOver(), canDrop: m.canDrop() }),
      drop: (item: any) => {
        if (item.type === "CHANNEL") {
          assignChannel(cell.cellId, item.deviceId, item.channel);
        } else if (item.type === "DEVICE") {
          const channels: number[] = item.channels || [];
          if (channels.length === 0) {
            toast.error("El dispositivo no tiene canales sincronizados");
            return;
          }
          const idxStart = cells.findIndex((c) => c.cellId === cell.cellId);
          let assigned = 0;
          for (let i = 0; i < channels.length && idxStart + i < cells.length; i++) {
            assignChannel(cells[idxStart + i].cellId, item.deviceId, channels[i]);
            assigned++;
          }
          if (assigned < channels.length) {
            toast(`Solo entraron ${assigned} de ${channels.length} canales en la grilla actual.`);
          }
        }
      },
    }),
    [cell.cellId, cells],
  );

  // Orquestador de streams: arranca uno nuevo cuando cambia (device,channel,subtype)
  // o limpia si la celda se vacia. Pone el stream en el slot opuesto al activo
  // cuando ya hay video reproduciendose (para que el viejo siga visible mientras
  // el nuevo carga); en el slot activo si el slot estaba vacio (primera carga).
  useEffect(() => {
    const dev = cell.deviceId;
    const ch = cell.channel;
    const st = cell.subtype as Subtype;

    if (dev == null || ch == null) {
      // Celda vaciada: detener todo
      if (slotA) {
        stopStream(slotA.streamId).catch(() => {});
        setSlotA(null);
      }
      if (slotB) {
        stopStream(slotB.streamId).catch(() => {});
        setSlotB(null);
      }
      setStreamInStore(cell.cellId, null, null);
      return;
    }

    const stillVisible = devices.some((d) => d.id === dev);
    if (!stillVisible) return;

    const active = activeSlot === "A" ? slotA : slotB;
    const pending = activeSlot === "A" ? slotB : slotA;
    const setPending = activeSlot === "A" ? setSlotB : setSlotA;
    const setActive = activeSlot === "A" ? setSlotA : setSlotB;

    // Ya tenemos lo que queremos en activo: limpiar pending obsoleto si lo hay.
    if (matchesCell(active)) {
      if (pending && !matchesCell(pending)) {
        stopStream(pending.streamId).catch(() => {});
        setPending(null);
      }
      return;
    }
    // Pending ya esta cargando lo que queremos: esperar a que reporte "playing".
    if (matchesCell(pending)) return;

    // Necesitamos iniciar nuevo. Antes, descartar pending obsoleto.
    if (pending) {
      stopStream(pending.streamId).catch(() => {});
      setPending(null);
    }

    let cancelled = false;
    startStream(dev, ch, st, { transcode })
      .then((res) => {
        if (cancelled) {
          stopStream(res.stream_id).catch(() => {});
          return;
        }
        const newStream: LiveStream = {
          streamId: res.stream_id,
          streamName: res.stream_name,
          subtype: st,
          deviceId: dev,
          channel: ch,
          transcode,
        };
        if (!active) {
          // Primera carga: directo al slot activo.
          setActive(newStream);
          setStreamInStore(cell.cellId, res.stream_id, res.stream_name);
        } else {
          // Hay video: cargar en slot pending y dejar que onStateChange haga swap.
          setPending(newStream);
        }
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        const msg = detail || e?.message || "error desconocido";
        toast.error(`Stream fallo (${status || "??"}): ${msg}`, { duration: 6000 });
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell.deviceId, cell.channel, cell.subtype, slotA, slotB, activeSlot, transcode]);

  // Cleanup al desmontar: detener ambos slots (los refs capturan los valores
  // actuales aun si el closure del cleanup quedo stale).
  useEffect(() => {
    return () => {
      if (slotARef.current) stopStream(slotARef.current.streamId).catch(() => {});
      if (slotBRef.current) stopStream(slotBRef.current.streamId).catch(() => {});
    };
  }, []);

  // Llamado por cada WebRTCPlayer cuando su state interno cambia.
  // - "playing" del pending → swap normal (mostrar el nuevo, parar el viejo).
  // - "error" del pending → también swap: el slot pending queda visible mostrando
  //   el error real (botón Reintentar), evitando que el usuario quede mirando
  //   "Activando re-codificación..." indefinidamente cuando el transcode falla.
  const onSlotStateChange = (slot: Slot, state: PlayerState) => {
    if (slot === activeSlot) return;
    if (state !== "playing" && state !== "error") return;
    const newActive = slot === "A" ? slotA : slotB;
    const oldActive = slot === "A" ? slotB : slotA;
    if (!newActive) return;
    setActiveSlot(slot);
    if (oldActive) {
      stopStream(oldActive.streamId).catch(() => {});
      if (slot === "A") setSlotB(null);
      else setSlotA(null);
    }
    setStreamInStore(cell.cellId, newActive.streamId, newActive.streamName);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const closeMenu = () => setContextMenu(null);

  // Cierra el menu al primer click afuera. setTimeout(0) evita que el mismo
  // contextMenu event que abrio el menu lo cierre inmediatamente.
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    const t = window.setTimeout(() => {
      window.addEventListener("click", handler, { once: true });
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener("click", handler);
    };
  }, [contextMenu]);

  const handleRemove = () => {
    if (slotARef.current) stopStream(slotARef.current.streamId).catch(() => {});
    if (slotBRef.current) stopStream(slotBRef.current.streamId).catch(() => {});
    setSlotA(null);
    setSlotB(null);
    clearCell(cell.cellId);
  };

  const device = devices.find((d) => d.id === cell.deviceId);
  const ch = device && cell.channel != null
    ? (channelsByDevice[device.id] || []).find((c) => c.channel_number === cell.channel)
    : null;
  const label = device && cell.channel != null
    ? `${device.name} CH${cell.channel}${ch?.name ? ` - ${ch.name}` : ""}`
    : "";

  return (
    <div
      ref={drop as any}
      className={clsx(
        "relative w-full h-full bg-black border border-line",
        isOver && canDrop && "ring-2 ring-accent",
        isSelected && "ring-2 ring-blue-500",
      )}
      onClick={() => setSelectedCell(cell.cellId)}
      onDoubleClick={onMaximize}
      onContextMenu={handleContextMenu}
    >
      {/* Slot A: posicion DOM fija, solo cambia visibilidad */}
      <div className="absolute inset-0" style={{ display: activeSlot === "A" ? "block" : "none" }}>
        {slotA && (
          <WebRTCPlayer
            streamName={slotA.streamName}
            label={label}
            showAudioToggle={true}
            fitMode={fitMode}
            onStateChange={(s) => onSlotStateChange("A", s)}
            onDecodeError={transcode ? undefined : () => {
              writeTranscodeFlag(transcodeKey);
              setTranscode(true);
            }}
            onTranscodeFailed={transcode ? () => {
              clearTranscodeFlag(transcodeKey);
              setTranscode(false);
            } : undefined}
          />
        )}
      </div>
      {/* Slot B */}
      <div className="absolute inset-0" style={{ display: activeSlot === "B" ? "block" : "none" }}>
        {slotB && (
          <WebRTCPlayer
            streamName={slotB.streamName}
            label={label}
            showAudioToggle={true}
            fitMode={fitMode}
            onStateChange={(s) => onSlotStateChange("B", s)}
            onDecodeError={transcode ? undefined : () => {
              writeTranscodeFlag(transcodeKey);
              setTranscode(true);
            }}
            onTranscodeFailed={transcode ? () => {
              clearTranscodeFlag(transcodeKey);
              setTranscode(false);
            } : undefined}
          />
        )}
      </div>

      {cell.deviceId != null && (
        <button
          className="absolute top-1 right-7 bg-black/60 text-white p-1 rounded hover:bg-red-600/80 z-20"
          onClick={(e) => { e.stopPropagation(); handleRemove(); }}
          title="Quitar canal"
        >
          <X size={11} />
        </button>
      )}

      {!cell.deviceId && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-muted text-xs">
          Arrastra un canal aqui
        </div>
      )}

      {cell.deviceId != null && !device && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-muted text-xs text-center px-2">
          Sin acceso a este dispositivo.<br/>Quita la celda o pide acceso a un admin.
        </div>
      )}

      {contextMenu && (
        <div
          className="fixed z-50 bg-panel border border-line rounded shadow-lg text-xs py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="px-3 py-1 text-muted uppercase tracking-wide">Calidad</div>
          <button
            className={clsx("w-full text-left px-3 py-1 hover:bg-line", cell.subtype === 0 && "text-accent")}
            onClick={(e) => { e.stopPropagation(); setSubtype(cell.cellId, 0, true); closeMenu(); }}
          >
            Principal (Main)
          </button>
          <button
            className={clsx("w-full text-left px-3 py-1 hover:bg-line", cell.subtype === 1 && "text-accent")}
            onClick={(e) => { e.stopPropagation(); setSubtype(cell.cellId, 1, true); closeMenu(); }}
          >
            Secundario (Sub)
          </button>
          {transcode && (
            <>
              <div className="border-t border-line my-1" />
              <button
                className="w-full text-left px-3 py-1 hover:bg-line text-amber-300"
                onClick={(e) => {
                  e.stopPropagation();
                  clearTranscodeFlag(transcodeKey);
                  setTranscode(false);
                  closeMenu();
                }}
                title="El canal esta marcado como HEVC indecodable; reintentar sin re-codificacion"
              >
                Reintentar sin transcode
              </button>
            </>
          )}
          <div className="border-t border-line my-1" />
          <button
            className="w-full text-left px-3 py-1 hover:bg-line text-red-400"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
              closeMenu();
            }}
          >
            Quitar canal
          </button>
        </div>
      )}
    </div>
  );
}
